import { createBrowserRouter, Link, Navigate } from 'react-router';
import { EmptyState } from '../shared/ui/components';
import { BibleHistoryPage } from '../features/bible/HistoryPage';
import { CallingLessonPage, CallingPage, CallingStagePage } from '../features/calling/CallingPages';
import { ConfirmEmailPage } from '../features/auth/ConfirmEmailPage';
import { ChildDevicePage } from '../features/auth/ChildDevicePage';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { LoginPage } from '../features/auth/LoginPage';
import { RegisterPage } from '../features/auth/RegisterPage';
import { BillingPage } from '../features/billing/BillingPage';
import { ReceiptPage } from '../features/billing/BillingHistory';
import { CalendarPage } from '../features/calendar/CalendarPage';
import { CreateChurchPage } from '../features/churches/CreateChurchPage';
import { HomePage } from '../features/home/HomePage';
import { LandingPage } from '../features/landing/LandingPage';
import { ProfilePage } from '../features/profile/ProfilePage';
import { HomeLovePage } from '../features/hogar/HomeLovePage';
import { RequireSession } from '../features/session/RequireSession';
import { AppLayout } from '../features/shell/AppLayout';
import { DesktopGuard } from '../features/presentation/DesktopGuard';
import { DeviceLinkPage } from '../features/presentation/DeviceLinkPage';
import { MorePage } from '../features/shell/MorePage';
import { HubPage } from '../features/shell/HubPage';
import { MyChurchPage } from '../features/shell/MyChurchPage';
import { ChurchStoryPage } from '../features/churches/ChurchStoryPage';
import { AdStatisticsPage } from '../features/denomination/AdStatisticsPage';
import { AdMinistriesPage } from '../features/denomination/AdMinistriesPage';
import { AdCredentialsPage } from '../features/denomination/AdCredentialsPage';
import { AdOfferingsPage } from '../features/denomination/AdOfferingsPage';
import { AdYouthPage } from '../features/denomination/AdYouthPage';
import { AdSundayPage } from '../features/denomination/AdSundayPage';
import { AdVbsPage } from '../features/denomination/AdVbsPage';
import { AdOverseerPage } from '../features/denomination/AdOverseerPage';
import { AnnouncementsPage } from '../features/communication/AnnouncementsPage';
import { PrayerPage } from '../features/prayer/PrayerPage';
import { ApprovalsPage } from '../features/approvals/ApprovalsPage';
import { FinancePage } from '../features/finance/FinancePage';
import { MyPledgesPage } from '../features/finance/MyPledgesPage';
import { FinanceImagePage } from '../features/finance/FinanceImagePage';
import { CommercePage } from '../features/commerce/CommercePage';
import { KitchenPage, TurnsDisplayPage } from '../features/commerce/Kitchen';
import { BiblePage } from '../features/bible/BiblePage';
import { DevotionalPage } from '../features/devotional/DevotionalPage';
import { MediaPage } from '../features/media/MediaPage';
import { PresentationsPage } from '../features/presentation/PresentationsPage';
import { ThemePage } from '../features/presentation/ThemePage';
import { SettingsPage } from '../features/presentation/SettingsPage';
import { RemotePage } from '../features/presentation/RemotePage';
import { PresentationBuilderPage } from '../features/presentation/PresentationBuilderPage';
import { ConsolePage } from '../features/presentation/ConsolePage';
import { OutputPage } from '../features/presentation/OutputPage';
import { ObsOutputPage } from '../features/presentation/ObsOutput';
import { ChurchSettingsPage } from '../features/settings-church/ChurchSettingsPage';
import { ReportsPage } from '../features/reports/ReportsPage';
import { NurseryPage } from '../features/nursery/NurseryPage';
import { PlanBuilderPage } from '../features/devotional/PlanBuilderPage';
import { PlanPage } from '../features/devotional/PlanPage';
import { CarePage } from '../features/care/CarePage';
import { TestimoniesPage } from '../features/community/TestimoniesPage';
import { CampaignPage, CampaignsPage } from '../features/community/CampaignPages';
import { PollsPage } from '../features/community/PollsPage';
import { ForumPage, ForumTopicPage } from '../features/community/ForumPages';
import { HelpPage, MissionsPage } from '../features/community/OutreachPages';
import { NetworkPage } from '../features/community/NetworkPage';
import { OrgChartPage } from '../features/ministries/OrgChartPage';
import { SearchPage } from '../features/search/SearchPage';
import { PrayerChainPage } from '../features/prayer/PrayerChains';
import { JournalPage } from '../features/prayer/JournalPage';
import { WalletPage } from '../features/wallet/WalletPage';
import { FundraiserPage, FundraisersPage, PublicFundraiserPage } from '../features/fundraisers/FundraiserPages';
import { MaintenanceDetailPage, MaintenancePage } from '../features/maintenance/MaintenancePages';
import { DailyPlannerPage } from '../features/devotional/DailyPlannerPage';
import { ReadingGroupPage } from '../features/devotional/ReadTogether';
import { GrowthPage } from '../features/devotional/GrowthPage';
import { NotebookPage } from '../features/devotional/NotebookPage';
import { LibraryPage } from '../features/media/LibraryPage';
import { ContentPage } from '../features/content/ContentPage';
import { NewPiecePage, PiecePage } from '../features/content/PiecePage';
import { PublicationPage, PublicationsPage } from '../features/publishing/PublicationsPage';
import { PublicChurchPage } from '../features/publishing/PublicChurchPage';
import { FindChurchPage } from '../features/site/FindChurchPage';
import { SiteSettingsPage } from '../features/site/SiteSettingsPage';
import { LivePage } from '../features/live/LivePage';
import { InventoryPage } from '../features/inventory/InventoryPage';
import { FillFormPage } from '../features/forms/FillFormPage';
import { FormBuilderPage } from '../features/forms/FormBuilderPage';
import { FormResponsesPage } from '../features/forms/FormResponsesPage';
import { FormsPage } from '../features/forms/FormsPage';
import { NotificationsPage } from '../features/notifications/NotificationsPage';
import { MyServicePage } from '../features/assignments/MyServicePage';
import { TeamPage } from '../features/assignments/TeamPage';
import { AttendancePage } from '../features/attendance/AttendancePage';
import { ServiceSettingsPage, WorshipPage } from '../features/worship/WorshipPage';
import { QrCheckInPage } from '../features/attendance/QrAttendance';
import { RouteError } from './RouteError';
import { PlansAdminPage } from '../features/plans/PlansAdminPage';
import { AdminChurchPage } from '../features/admin/AdminChurchPage';
import { AdminChurchesPage } from '../features/admin/AdminChurchesPage';
import { AdminUsersPage } from '../features/admin/AdminUsersPage';
import { MemberPage } from '../features/members/MemberPage';
import { MembersPage } from '../features/members/MembersPage';
import { MinistriesPage } from '../features/ministries/MinistriesPage';
import { MinistryPage } from '../features/ministries/MinistryPage';
import { ChatsPage } from '../features/chats/ChatsPage';
import { PersonProfilePage } from '../features/people/PersonProfilePage';
import { EventsPage } from '../features/events/EventsPage';
import { MeetingsPage } from '../features/meetings/MeetingsPage';
import { VocationPage } from '../features/vocation/VocationPage';
import { OrdinancesPage } from '../features/ordinances/OrdinancesPage';
import { CampsPage } from '../features/camps/CampsPage';
import { CampPage } from '../features/camps/CampPage';
import { SongsPage } from '../features/songs/SongsPage';
import { RehearsalPage, RehearsalsPage } from '../features/worship/RehearsalPages';
import { SongPage } from '../features/songs/SongPage';
import { ServiceSongsPage } from '../features/songs/ServiceSongsPage';
import { ServiceProgramPage } from '../features/worship/ServiceProgramPage';
import { OrderTemplatePage } from '../features/worship/ServiceOrder';
import { VisitorPage } from '../features/visitors/VisitorPage';
import { PublicVisitorPage } from '../features/visitors/PublicVisitorPage';
import { JoinByInvitePage } from '../features/churches/JoinByInvitePage';
import { ClassroomsPage } from '../features/classrooms/ClassroomsPage';
import { PrayerDayFormPage, PrayerDayPage, PrayerPrintPage } from '../features/prayer/PrayerDayPages';
import { ClassroomPage } from '../features/classrooms/ClassroomPage';
import { SessionPage } from '../features/classrooms/SessionPage';
import { TeacherPanelPage } from '../features/classrooms/TeacherPanel';
import { ClassScreenPage } from '../features/classrooms/ClassScreen';
import { QuizPrintPage } from '../features/classrooms/QuizPrintPage';
import { MeetingFormPage } from '../features/meetings/MeetingFormPage';
import { MeetingPage } from '../features/meetings/MeetingPage';
import { EventFormPage } from '../features/events/EventFormPage';
import { EventPage } from '../features/events/EventPage';
import { PublicEventPage } from '../features/events/PublicEventPage';
import { PublicMeetingPage } from '../features/events/PublicMeetingPage';
import { PrivacyPage } from '../features/legal/PrivacyPage';
import { TermsPage } from '../features/legal/TermsPage';
import { MemoryPage, VerseImagePage } from '../features/bible/Study';
import { DictionaryEntryPage, DictionaryPage } from '../features/bible/Dictionary';
import { TopicPage, TopicsPage } from '../features/bible/Topics';
import { TriviaHostPage, TriviaJoinPage, TriviaPage, TriviaPlayPage } from '../features/bible/Trivia';
import { MinutesPage } from '../features/bible/Minutes';
import { MinutesAdminPage } from '../features/bible/MinutesAdmin';
import { BibleStudyHubPage, MapPage, PeoplePage, PersonPage, PlacePage, TimelineEventPage, TimelinePage } from '../features/bible/Atlas';

/** Rutas en español: son lo que ve el usuario. Una pantalla no equivale a una tabla. */
export const routes = [
  {
    // En la app KEORA Multimedia no se ve el sitio: solo la entrada del equipo y Presentaciones.
    element: <DesktopGuard />,
    errorElement: <RouteError />,
    children: [
  { path: '/equipo', element: <DeviceLinkPage /> },
  { path: '/', element: <LandingPage /> },
  { path: '/entrar', element: <LoginPage /> },
  { path: '/privacidad', element: <PrivacyPage /> },
  { path: '/terminos', element: <TermsPage /> },
  { path: '/registro', element: <RegisterPage /> },
  { path: '/confirmar-correo', element: <ConfirmEmailPage /> },
  { path: '/olvide-contrasena', element: <ForgotPasswordPage /> },
  { path: '/hijo', element: <ChildDevicePage /> },
  { path: '/restablecer-contrasena', element: <ResetPasswordPage /> },
  { path: '/iglesias', element: <FindChurchPage /> },
  { path: '/i/:slug', element: <PublicChurchPage /> },
  { path: '/e/:token', element: <PublicEventPage /> },
  { path: '/reunion/:token', element: <PublicMeetingPage /> },
  { path: '/visita/:slug', element: <PublicVisitorPage /> },
  { path: '/unirse/:churchId', element: <JoinByInvitePage /> },
  { path: '/r/:slug/:token', element: <PublicFundraiserPage /> },
  { path: '/obs/:token', element: <ObsOutputPage /> },
  { path: '/turnos/:token', element: <TurnsDisplayPage /> },
  { path: '/trivia', element: <TriviaJoinPage /> },
  { path: '/trivia/jugar/:token', element: <TriviaPlayPage /> },
  { path: '/trivia/:code', element: <TriviaJoinPage /> },
  { path: '/i/:slug/:publicationId', element: <PublicChurchPage /> },
  {
    path: '/app',
    element: <RequireSession />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'perfil', element: <ProfilePage /> },
          { path: 'hogar', element: <HomeLovePage /> },
          { path: 'solicitudes', element: <Navigate to="/app/miembros?tab=solicitudes" replace /> },
          { path: 'miembros', element: <MembersPage /> },
          { path: 'ministerios', element: <MinistriesPage /> },
          { path: 'chats', element: <ChatsPage /> },
          { path: 'personas/:personId', element: <PersonProfilePage /> },
          { path: 'chats/:channelId', element: <ChatsPage /> },
          { path: 'ministerios/:ministryId', element: <MinistryPage /> },
          { path: 'ministerios/:ministryId/reuniones/nueva', element: <MeetingFormPage /> },
          { path: 'reuniones', element: <MeetingsPage /> },
          { path: 'visitantes', element: <Navigate to="/app/miembros?tab=visitas" replace /> },
          { path: 'visitantes/:visitorId', element: <VisitorPage /> },
          { path: 'salones', element: <ClassroomsPage /> },
          { path: 'salones/panel', element: <TeacherPanelPage /> },
          { path: 'salones/panel/:classroomId', element: <TeacherPanelPage /> },
          { path: 'salones/:classroomId', element: <ClassroomPage /> },
          { path: 'salones/:classroomId/clases/:sessionId', element: <SessionPage /> },
          { path: 'reuniones/:meetingId', element: <MeetingPage /> },
          { path: 'reuniones/:meetingId/editar', element: <MeetingFormPage /> },
          { path: 'miembros/:personId', element: <MemberPage /> },
          { path: 'iglesias/nueva', element: <CreateChurchPage /> },
          { path: 'facturacion', element: <BillingPage /> },
          { path: 'planes', element: <PlansAdminPage /> },
          { path: 'sitio', element: <SiteSettingsPage /> },
          { path: 'en-vivo', element: <LivePage /> },
          { path: 'admin/iglesias', element: <AdminChurchesPage /> },
          { path: 'admin/usuarios', element: <AdminUsersPage /> },
          { path: 'admin/iglesias/:churchId', element: <AdminChurchPage /> },
          { path: 'calendario', element: <CalendarPage /> },
          { path: 'eventos', element: <EventsPage /> },
          { path: 'eventos/nuevo', element: <EventFormPage /> },
          { path: 'eventos/:eventId', element: <EventPage /> },
          { path: 'eventos/:eventId/editar', element: <EventFormPage /> },
          { path: 'cultos', element: <WorshipPage /> },
          { path: 'cultos/configurar', element: <ServiceSettingsPage /> },
          { path: 'cultos/orden', element: <OrderTemplatePage /> },
          { path: 'cultos/:serviceId', element: <ServiceProgramPage /> },
          { path: 'cultos/:serviceId/asistencia', element: <AttendancePage /> },
          { path: 'asistencia/qr/:token', element: <QrCheckInPage /> },
          { path: 'cultos/:serviceId/equipo', element: <TeamPage /> },
          { path: 'cultos/:serviceId/cantos', element: <ServiceSongsPage /> },
          { path: 'cantos', element: <SongsPage /> },
          { path: 'ensayos', element: <RehearsalsPage /> },
          { path: 'ensayos/:rehearsalId', element: <RehearsalPage /> },
          { path: 'cantos/nuevo', element: <SongPage /> },
          { path: 'cantos/:songId', element: <SongPage /> },
          { path: 'servicio', element: <MyServicePage /> },
          { path: 'vocacion', element: <VocationPage /> },
          { path: 'servir', element: <Navigate to="/app/vocacion" replace /> },
          { path: 'consejeria', element: <CarePage /> },
          { path: 'testimonios', element: <TestimoniesPage /> },
          { path: 'campanas', element: <CampaignsPage /> },
          { path: 'encuestas', element: <PollsPage /> },
          { path: 'foro', element: <ForumPage /> },
          { path: 'ayuda', element: <HelpPage /> },
          { path: 'red', element: <NetworkPage /> },
          { path: 'misiones', element: <MissionsPage /> },
          { path: 'foro/:topicId', element: <ForumTopicPage /> },
          { path: 'campanas/:campaignId', element: <CampaignPage /> },
          { path: 'organigrama', element: <OrgChartPage /> },
          { path: 'buscar', element: <SearchPage /> },
          { path: 'monedero', element: <WalletPage /> },
          { path: 'recaudaciones', element: <FundraisersPage /> },
          { path: 'recaudaciones/:fundraiserId', element: <FundraiserPage /> },
          { path: 'mantenimiento', element: <MaintenancePage /> },
          { path: 'mantenimiento/:requestId', element: <MaintenanceDetailPage /> },
          { path: 'ordenanzas', element: <OrdinancesPage /> },
          { path: 'campamentos', element: <CampsPage /> },
          { path: 'campamentos/:campId', element: <CampPage /> },
          { path: 'avisos', element: <AnnouncementsPage /> },
          { path: 'oracion', element: <PrayerPage /> },
          { path: 'oracion/cadenas/:chainId', element: <PrayerChainPage /> },
          { path: 'oracion/diario', element: <JournalPage /> },
          { path: 'oracion/jornadas/nueva', element: <PrayerDayFormPage /> },
          { path: 'oracion/jornadas/:sessionId', element: <PrayerDayPage /> },
          { path: 'oracion/jornadas/:sessionId/editar', element: <PrayerDayFormPage /> },
          { path: 'formularios', element: <FormsPage /> },
          { path: 'formularios/nuevo', element: <FormBuilderPage /> },
          { path: 'formularios/:formId', element: <FillFormPage /> },
          { path: 'formularios/:formId/editar', element: <FormBuilderPage /> },
          { path: 'formularios/:formId/respuestas', element: <FormResponsesPage /> },
          { path: 'aprobaciones', element: <ApprovalsPage /> },
          { path: 'finanzas', element: <FinancePage /> },
          { path: 'promesas', element: <MyPledgesPage /> },
          { path: 'finanzas/imagen', element: <FinanceImagePage /> },
          { path: 'inventario', element: <InventoryPage /> },
          { path: 'comercio', element: <CommercePage /> },
          { path: 'contenido', element: <ContentPage /> },
          { path: 'contenido/nueva', element: <NewPiecePage /> },
          { path: 'contenido/:pieceId', element: <PiecePage /> },
          { path: 'llamado', element: <CallingPage /> },
          { path: 'llamado/:stageKey', element: <CallingStagePage /> },
          { path: 'llamado/:stageKey/:lessonKey', element: <CallingLessonPage /> },
          { path: 'biblia', element: <BiblePage /> },
          { path: 'biblia/memorizar', element: <MemoryPage /> },
          { path: 'biblia/estudiar', element: <BibleStudyHubPage /> },
          { path: 'biblia/historia', element: <BibleHistoryPage /> },
          { path: 'biblia/trivia', element: <TriviaPage /> },
          { path: 'biblia/minutos', element: <MinutesPage /> },
          { path: 'biblia/minutos/contenido', element: <MinutesAdminPage /> },
          { path: 'biblia/minutos/iglesia', element: <MinutesAdminPage scope="church" /> },
          { path: 'biblia/temas', element: <TopicsPage /> },
          { path: 'biblia/diccionario', element: <DictionaryPage /> },
          { path: 'biblia/diccionario/:entryId', element: <DictionaryEntryPage /> },
          { path: 'biblia/temas/:topicId', element: <TopicPage /> },
          { path: 'biblia/imagen', element: <VerseImagePage /> },
          { path: 'biblia/personajes', element: <PeoplePage /> },
          { path: 'biblia/personajes/:personId', element: <PersonPage /> },
          { path: 'biblia/mapa', element: <MapPage /> },
          { path: 'biblia/lugares/:placeId', element: <PlacePage /> },
          { path: 'biblia/cronologia', element: <TimelinePage /> },
          { path: 'biblia/cronologia/:eventId', element: <TimelineEventPage /> },
          { path: 'devocional', element: <DevotionalPage /> },
          { path: 'devocional/nuevo', element: <PlanBuilderPage /> },
          { path: 'devocional/hoy', element: <DailyPlannerPage /> },
          { path: 'devocional/juntos/:groupId', element: <ReadingGroupPage /> },
          { path: 'crecimiento', element: <GrowthPage /> },
          { path: 'cuaderno', element: <NotebookPage /> },
          { path: 'biblioteca', element: <LibraryPage /> },
          { path: 'devocional/:planId', element: <PlanPage /> },
          { path: 'devocional/:planId/editar', element: <PlanBuilderPage /> },
          { path: 'multimedia', element: <MediaPage /> },
          { path: 'automatizacion', element: <Navigate to="/app/configuracion-iglesia#recordatorios" replace /> },
          { path: 'configuracion-iglesia', element: <ChurchSettingsPage /> },
          { path: 'reportes', element: <ReportsPage /> },
          { path: 'guarderia', element: <NurseryPage /> },
          { path: 'presentaciones', element: <PresentationsPage /> },
          { path: 'configuracion', element: <SettingsPage /> },
          { path: 'presentaciones/nueva', element: <PresentationBuilderPage /> },
          { path: 'presentaciones/tema', element: <ThemePage /> },
          { path: 'presentaciones/:presentationId', element: <ConsolePage /> },
          { path: 'presentaciones/:presentationId/editar', element: <PresentationBuilderPage /> },
          { path: 'presentaciones/:presentationId/control', element: <RemotePage /> },
          { path: 'publicaciones', element: <PublicationsPage /> },
          { path: 'publicaciones/:publicationId', element: <PublicationPage /> },
          { path: 'notificaciones', element: <NotificationsPage /> },
          { path: 'mas', element: <MorePage /> },
          { path: 'mi-iglesia', element: <MyChurchPage /> },
          { path: 'nuestra-historia', element: <ChurchStoryPage /> },
          { path: 'lideres', element: <HubPage title="Dirigir" /> },
          { path: 'palabra', element: <HubPage title="Palabra" /> },
          { path: 'comunidad', element: <HubPage title="Comunidad" /> },
          { path: 'herramientas', element: <HubPage title="Culto y medios" /> },
          { path: 'administracion', element: <HubPage title="Administración" /> },
          { path: 'panel', element: <HubPage title="Panel KEORA" /> },
          { path: 'asambleas', element: <HubPage title="Asambleas de Dios" /> },
          { path: 'asambleas/informe', element: <AdStatisticsPage /> },
          { path: 'asambleas/ministerios', element: <AdMinistriesPage /> },
          { path: 'asambleas/credenciales', element: <AdCredentialsPage /> },
          { path: 'asambleas/ofrendas', element: <AdOfferingsPage /> },
          { path: 'asambleas/escuela-dominical', element: <AdSundayPage /> },
          { path: 'asambleas/ebv', element: <AdVbsPage /> },
          { path: 'asambleas/presbiterio', element: <AdOverseerPage /> },
          { path: 'asambleas/exploradores', element: <AdYouthPage program="exploradores" /> },
          { path: 'asambleas/misioneritas', element: <AdYouthPage program="misioneritas" /> },
          // Un enlace viejo dentro de la app no debe sacar a la persona a la portada.
          { path: '*', element: <MissingPage /> },
        ],
      },
      // Salidas de Presentación: pantalla completa, sin menús.
      { path: 'pantalla/:churchId/:presentationId/:screen', element: <OutputPage /> },
      // Pantalla de cocina de la cafetería (tablet o tele), sin menús.
      { path: 'cocina', element: <KitchenPage /> },
      // Trivia en vivo: pantalla del líder para proyectar.
      { path: 'trivia/juego/:gameId', element: <TriviaHostPage /> },
      // Hoja para imprimir o guardar como PDF, sin menús.
      { path: 'oracion/imprimir', element: <PrayerPrintPage /> },
      // Clase de Raíces en vivo para proyectar (la misma que la fuente «Clase en vivo» de Multimedia).
      { path: 'pantalla-clase/:churchId/:classroomId/:view', element: <ClassScreenPage /> },
      { path: 'salones/:classroomId/cuestionarios/:quizId/imprimir', element: <QuizPrintPage /> },
      { path: 'facturacion/recibo/:eventId', element: <ReceiptPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

function MissingPage() {
  return (
    <div className="page">
      <EmptyState title="Esa página ya no existe.">
        <Link className="btn btn--primary" to="/app">
          Ir a Inicio
        </Link>
      </EmptyState>
    </div>
  );
}

export const createRouter = () => createBrowserRouter(routes);
